import _ from 'lodash';
import moment from 'moment-timezone';
import {
  sqAggregationBinStore,
  sqClusteringStoreV2,
  sqCompositeSearchStore,
  sqConditionWithPropertiesStore,
  sqDigitalFilterStore,
  sqDisplayToolStore,
  sqDurationStore,
  sqExportExcelPanelStore,
  sqFftStore,
  sqImportDatafileStore,
  sqInvestigateStore,
  sqIsolationForestStoreV2,
  sqItemPropertiesStore,
  sqManualConditionStore,
  sqOnnxStore,
  sqOperationalizeStatisticsSummaryStore,
  sqPartialLeastSquaresStoreV2,
  sqPeriodicConditionStore,
  sqPredictionPanelStore,
  sqPrincipalComponentAnalysisStoreV2,
  sqProfileSearchStore,
  sqReferencePanelStore,
  sqScatterConditionStore,
  sqSelfOrganizingMapsStoreV2,
  sqSignalForecastStore,
  sqSignalFromConditionStore,
  sqSignalSmoothingStore,
  sqStatisticsSummaryStore,
  sqTableBuilderStore,
  sqThresholdMetricStore,
  sqToolTemplateStore,
  sqTrendStore,
  sqViewStatisticSummariesStore,
  sqWorkbenchStore,
  sqWorkbookStore,
  sqWorksheetStore,
} from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { DISPLAY_MODE } from '@/main/app.constants';
import { getAxiosInstance } from '@/requests/axios.utilities';
import { TableBuilderMode } from '@/tableBuilder/tableBuilder.constants';
import { DEFAULT_WINDOW_DETAILS, focusOrOpenNamedWindow, prepareTargetUrl } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { generateDefaultGridSize } from '@/tools/exportExcel/exportExcelPanel.utilities';
import { DEFAULT_TOOL_ITEM_PROPS } from '@/tools/tool.constants';
import { shouldResetToolStore } from '@/tools/tool.utilities';
import { getAllItems } from '@/trend/trendDataHelper.utilities';
import { ITEM_TYPES } from '@/trendData/trendData.constants';
import { isItemRedacted } from '@/utilities/redaction.utilities';
import { ProcessTypeEnum } from '@seeqdev/api/model/ThresholdMetricOutputV1';
import { SeeqNames } from '@seeqdev/constants';
import type { PushOption } from '@/core/flux.service';
import type { InvestigateToolType } from '@/toolSelection/investigate.constants';
import type { ExportExcelPanelState } from '@/tools/exportExcel/exportExcelPanel.store';
import type { ToolItem } from '@/tools/tool.types';

// File created to assist in mocking the functions in investigate.actions.test

/**
 * Builds the reset overrides for the Export to Excel store when its tool is opened or when Investigate closes to
 * OVERVIEW. Signals/conditions are only populated when Export to Excel is the tool being opened, mirroring the old
 * Flux INVESTIGATE_SET_DISPLAY_MODE handler.
 */
function buildExportExcelResetOverrides(isOpeningExportExcel: boolean): Partial<ExportExcelPanelState> {
  const getExportItems = (itemType: ITEM_TYPES): ToolItem[] =>
    _.chain(getAllItems({ workingSelection: true, itemTypes: [itemType] }))
      .reject(isItemRedacted)
      .map((item) => _.pick(item, DEFAULT_TOOL_ITEM_PROPS) as ToolItem)
      .value();

  return {
    name: `${sqWorkbookStore.name}_${sqWorkbookStore.getWorksheetName(sqWorkbenchStore.stateParams.worksheetId)}`,
    timeRange: {
      start: sqDurationStore.displayRange.start.valueOf(),
      end: sqDurationStore.displayRange.end.valueOf(),
    },
    exportTimeZone: sqWorksheetStore.timezone,
    gridSize: generateDefaultGridSize(
      sqDurationStore.displayRange.end.valueOf() - sqDurationStore.displayRange.start.valueOf(),
    ),
    gridOrigin: moment().tz(sqWorksheetStore.timezone.name).startOf('year').valueOf(),
    chainView: sqTrendStore.isTrendViewChainView(),
    capsuleTime: sqTrendStore.isTrendViewCapsuleTime(),
    ...(isOpeningExportExcel
      ? { exportSignals: getExportItems(ITEM_TYPES.SERIES), exportConditions: getExportItems(ITEM_TYPES.CONDITION) }
      : {}),
  };
}

/**
 * Execute the action specified by the add-on tool linkType property
 */
export function executeAddOnTool(tool: InvestigateToolType) {
  const targetUrl = prepareTargetUrl(tool.targetUrl as string);

  switch (tool.linkType) {
    case SeeqNames.API.AddOnToolLinkType.Window: {
      const details = tool.windowDetails || DEFAULT_WINDOW_DETAILS;
      if (tool.reuseWindow) {
        const windowName = `${sqWorkbenchStore.interactiveSessionId}_${_.snakeCase(tool.name)}`;
        focusOrOpenNamedWindow(targetUrl, windowName, details);
      } else {
        window.open(targetUrl, '_blank', details);
      }
      break;
    }
    case SeeqNames.API.AddOnToolLinkType.Tab:
      window.open(targetUrl);
      break;
    case SeeqNames.API.AddOnToolLinkType.None:
      getAxiosInstance().get(targetUrl);
      break;
    default:
      throw new TypeError(`${tool.linkType} is not a valid linkType`);
  }
}

/**
 * Sets the display mode.
 *
 * @param {String} mode - One of DISPLAY_MODE
 * @param {String} [option] - Option for dispatch, such as PUSH_IGNORE
 */
export function setDisplayMode(mode: string, option?: string) {
  option = _.isUndefined(option) && mode === DISPLAY_MODE.IN_PROGRESS ? PUSH_IGNORE : undefined;
  if (!_.includes(_.values(DISPLAY_MODE), mode)) {
    throw new TypeError(`${mode} is not a valid mode`);
  }

  const activeTool = sqInvestigateStore.activeTool;
  // Legacy Flux stores still depend on this dispatch. BaseToolStore listens for INVESTIGATE_SET_DISPLAY_MODE and resets
  // on NEW when the payload type matches the store's tool type, or when the payload type is OVERVIEW.
  flux.dispatch('INVESTIGATE_SET_DISPLAY_MODE', { mode, type: activeTool }, option as PushOption);

  // Migrated Zustand tool stores do not observe Flux dispatches, so reset them explicitly here. Add each newly migrated
  // tool store as another `if` using shouldResetToolStore(...) when it needs the old BaseToolStore
  // INVESTIGATE_SET_DISPLAY_MODE behavior, plus a matching case in migratedToolStoreResetCases in
  // toolSelection/test/investigate.actions.test.ts.
  if (shouldResetToolStore(TREND_TOOLS.PROPERTIES, { mode, type: activeTool })) {
    sqItemPropertiesStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.DISPLAY, { mode, type: activeTool })) {
    sqDisplayToolStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.IMPORTDATAFILE, { mode, type: activeTool })) {
    sqImportDatafileStore.actions.reset();
  }

  if (shouldResetToolStore(TREND_TOOLS.EXPORT_EXCEL, { mode, type: activeTool })) {
    sqExportExcelPanelStore.actions.reset(buildExportExcelResetOverrides(activeTool === TREND_TOOLS.EXPORT_EXCEL));
  }

  if (shouldResetToolStore(TREND_TOOLS.DIGITAL_FILTER, { mode, type: activeTool })) {
    sqDigitalFilterStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.FFT_TABLE, { mode, type: activeTool })) {
    sqFftStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.AGGREGATION_BINS_TABLE, { mode, type: activeTool })) {
    sqAggregationBinStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.COMPOSITE_SEARCH, { mode, type: activeTool })) {
    sqCompositeSearchStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.ONNX, { mode, type: activeTool })) {
    sqOnnxStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.OPERATIONALIZE_STATISTICS_SUMMARY, { mode, type: activeTool })) {
    sqOperationalizeStatisticsSummaryStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.SIGNAL_FORECAST, { mode, type: activeTool })) {
    sqSignalForecastStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.VIEW_STATISTIC_SUMMARIES, { mode, type: activeTool })) {
    sqViewStatisticSummariesStore.actions.reset();
  }
  if (
    shouldResetToolStore(TREND_TOOLS.MODEL_CREATE, { mode, type: activeTool }) ||
    shouldResetToolStore(TREND_TOOLS.MODEL_APPLY, { mode, type: activeTool }) ||
    shouldResetToolStore(TREND_TOOLS.MODEL_EDIT, { mode, type: activeTool })
  ) {
    sqToolTemplateStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.CLUSTERING_V_2, { mode, type: activeTool })) {
    sqClusteringStoreV2.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.ISOLATION_FOREST_V_2, { mode, type: activeTool })) {
    sqIsolationForestStoreV2.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.PARTIAL_LEAST_SQUARES_V_2, { mode, type: activeTool })) {
    sqPartialLeastSquaresStoreV2.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.PRINCIPAL_COMPONENT_ANALYSIS_V_2, { mode, type: activeTool })) {
    sqPrincipalComponentAnalysisStoreV2.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.SELF_ORGANIZING_MAPS_V_2, { mode, type: activeTool })) {
    sqSelfOrganizingMapsStoreV2.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.STATISTICS_SUMMARY, { mode, type: activeTool })) {
    sqStatisticsSummaryStore.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.THRESHOLD_METRIC, { mode, type: activeTool })) {
    sqThresholdMetricStore.actions.reset({
      processType:
        sqTableBuilderStore.mode === TableBuilderMode.Simple ? ProcessTypeEnum.Simple : ProcessTypeEnum.Condition,
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.PREDICTION, { mode, type: activeTool })) {
    sqPredictionPanelStore.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.REFERENCE, { mode, type: activeTool })) {
    sqReferencePanelStore.actions.reset({
      windowStart: sqDurationStore.displayRange.start.valueOf(),
      windowEnd: sqDurationStore.displayRange.end.valueOf(),
    });
  }
  if (shouldResetToolStore(TREND_TOOLS.PROFILE_SEARCH, { mode, type: activeTool })) {
    sqProfileSearchStore.actions.reset({ selectedRegion: sqProfileSearchStore.getState().selectedRegion });
  }
  if (shouldResetToolStore(TREND_TOOLS.SCATTER_CONDITION, { mode, type: activeTool })) {
    sqScatterConditionStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.SIGNAL_FROM_CONDITION, { mode, type: activeTool })) {
    sqSignalFromConditionStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.SIGNAL_SMOOTHING, { mode, type: activeTool })) {
    sqSignalSmoothingStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.MANUAL_CONDITION, { mode, type: activeTool })) {
    sqManualConditionStore.actions.reset();
  }
  if (shouldResetToolStore(TREND_TOOLS.PERIODIC_CONDITION, { mode, type: activeTool })) {
    sqPeriodicConditionStore.actions.reset({ timezone: sqWorkbenchStore.userTimeZone?.name });
  }
  if (shouldResetToolStore(TREND_TOOLS.CONDITION_WITH_PROPERTIES, { mode, type: activeTool })) {
    sqConditionWithPropertiesStore.actions.reset();
  }
}
